import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import Actions from './Actions';

const CustomerSupportSection = () => {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [qq, setQQ] = useState('');
  const [wechat, setWeChat] = useState('');
  const [telegram, setTelegram] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const data = await api.get<{ value: string }>(
        '/backoffice/content/customerSupport',
      );
      const parsed = JSON.parse(data.value);
      setEmail(parsed.email || '');
      setQQ(parsed.qq || '');
      setWeChat(parsed.wechat || '');
      setTelegram(parsed.telegram || '');
      setDirty(false);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('config.support_email_invalid'));
      return;
    }

    setSaving(true);

    try {
      await api.put('/backoffice/content/customerSupport', {
        value: JSON.stringify({ email, qq, wechat, telegram }),
      });
      setDirty(false);
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const markDirty = () => setDirty(true);

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-5">
          <Mail className="w-5 h-5 text-foreground" />
          <h3 className="text-base font-medium text-foreground">
            {t('config.customer_support')}
          </h3>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {t('config.support_email')}
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                markDirty();
              }}
              placeholder={t('config.support_email_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {t('config.qq')}
            </label>
            <Input
              type="text"
              value={qq}
              onChange={(e) => {
                setQQ(e.target.value);
                markDirty();
              }}
              placeholder={t('config.qq_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {t('config.wechat')}
            </label>
            <Input
              type="text"
              value={wechat}
              onChange={(e) => {
                setWeChat(e.target.value);
                markDirty();
              }}
              placeholder={t('config.wechat_placeholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Telegram
            </label>
            <Input
              type="text"
              value={telegram}
              onChange={(e) => {
                setTelegram(e.target.value);
                markDirty();
              }}
              placeholder={t('config.telegram_placeholder')}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('config.support_hint')}
          </p>
        </div>

        <Actions
          onCancel={load}
          onSave={handleSave}
          saving={saving}
          disabled={!dirty}
        />
      </CardContent>
    </Card>
  );
};

export default CustomerSupportSection;
